import { useCallback, useEffect, useRef, useState } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { getActiveContext, subscribe } from '@/lib/active-context-store';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-notifications';

/**
 * The caller's unread «Note spese» notifications, for the bell (#865, decision 197 A.3, #861: whoever a
 * note's event touches is told in the console as well as by email).
 *
 * NO UNREAD FILTER, NO COUNT ENDPOINT. The API lists the caller's notifications newest first, read
 * and unread together, up to 50 a page (its contract,
 * `docs/specifications/expenses-release-2-notifications.openapi.yaml`, has nothing else). So the
 * bell follows `nextCursor` until it holds 50 unread or the list ends, and reads at most 5 pages:
 * 50 or more unread is `capped`, and the count says «50+». When 5 pages end with more still to
 * read, the count is `truncated`: «N+», «at least N», never a count that looks exact.
 *
 * THE STORE FIRST. api-client fills `X-Active-*` from the active-context store, and
 * ActiveContextProvider writes that store in an effect that runs AFTER this hook's own (a parent's
 * effects follow its children's). So a read or a mark is deferred by a microtask, and goes out only
 * once the store holds the organization it is for; an answer whose organization is no longer the
 * store's is dropped (as `useFinanceCapability` does). Otherwise a reload asks with no headers (a
 * 400), and a switch asks, or marks, in the previous organization.
 *
 * WHEN IT RE-READS: at mount and on a change of organization, when the panel opens (`reload`), and
 * when the window gets focus back. No polling.
 *
 * THREE STATES, AND A RE-READ IS NONE OF THEM: it is `refreshing`, on the loaded state. `loading`
 * until the first read answers, so the bell never says «nothing» too early; `ready`; `unavailable`
 * when the first read failed (503, network) or the active context did, which is not «nothing»
 * either. A later read that fails keeps what is on screen (ui-design-guidelines § Refreshing is not
 * loading). No expenses access (403), or no organization, is `ready` with nothing: there is nothing
 * of theirs to tell.
 */
export type ExpenseNotification = components['schemas']['Notification'];

/** The page size the server allows at most; also the cap of the count. */
export const NOTIFICATIONS_PAGE = 50;
/** How many pages one read follows at most. */
export const NOTIFICATIONS_MAX_PAGES = 5;

export type ExpenseNotificationsStatus = 'loading' | 'ready' | 'unavailable';

export type ExpenseNotificationsState = {
  readonly status: ExpenseNotificationsStatus;
  /** At most `NOTIFICATIONS_PAGE`, newest first. */
  readonly unread: readonly ExpenseNotification[];
  /** 50 or more unread: the count reads «50+». */
  readonly capped: boolean;
  /** The page bound ended the read with more to read: the count is a floor, «N+». */
  readonly truncated: boolean;
  /** A re-read is on its way; what is shown stays, and stays usable. */
  readonly refreshing: boolean;
  readonly reload: () => void;
  /** «Riprova»: reads the notifications again, or the active context first when that failed. */
  readonly retry: () => void;
  /** Marks one read: it leaves the list at once, and the server is told. */
  readonly markRead: (id: string) => void;
};

type Page = {
  readonly available: boolean;
  readonly unread: readonly ExpenseNotification[];
  readonly capped: boolean;
  readonly truncated: boolean;
};

const EMPTY: Page = { available: true, unread: [], capped: false, truncated: false };
const UNAVAILABLE: Page = { available: false, unread: [], capped: false, truncated: false };

/** The organization api-client asks for: the one in the active-context store. */
const storeOrg = (): string | undefined => getActiveContext()?.organizationId;

/**
 * The unread, following the pages; null when a page could not be read, or when the store no longer
 * holds `org` (the answer would be dropped: no page is asked for in another organization).
 */
async function readUnread(org: string): Promise<Page | null> {
  const unread: ExpenseNotification[] = [];
  let cursor: string | undefined;
  try {
    for (let pages = 0; pages < NOTIFICATIONS_MAX_PAGES; pages++) {
      if (storeOrg() !== org) return null;
      const { data, response } = await apiClient.GET('/v1/expenses/notifications', {
        params: {
          query:
            cursor === undefined
              ? { limit: NOTIFICATIONS_PAGE }
              : { cursor, limit: NOTIFICATIONS_PAGE },
        },
      });
      if (response.status === 403) return EMPTY;
      if (!response.ok || data === undefined) return null;
      unread.push(...data.items.filter((item) => item.readAt === null));
      // More than 50, or 50 with more still to read: «50+». Exactly 50 and the end is a count.
      if (
        unread.length > NOTIFICATIONS_PAGE ||
        (unread.length === NOTIFICATIONS_PAGE && data.nextCursor !== null)
      ) {
        return {
          available: true,
          unread: unread.slice(0, NOTIFICATIONS_PAGE),
          capped: true,
          truncated: false,
        };
      }
      if (data.nextCursor === null) {
        return { available: true, unread, capped: false, truncated: false };
      }
      cursor = data.nextCursor;
    }
  } catch {
    return null;
  }
  // The page bound, with a cursor still to follow.
  return { available: true, unread, capped: false, truncated: true };
}

/** Whether the server marked it; `stale` when the store no longer holds `org`, so nothing was sent. */
async function postRead(id: string, org: string): Promise<boolean | 'stale'> {
  try {
    await Promise.resolve();
    if (storeOrg() !== org) return 'stale';
    const { response } = await apiClient.POST('/v1/expenses/notifications/{notificationId}/read', {
      params: { path: { notificationId: id } },
    });
    // 404: not the caller's or gone; either way it is no longer an unread one of theirs.
    return response.ok || response.status === 404;
  } catch {
    return false;
  }
}

export function useExpenseNotifications(): ExpenseNotificationsState {
  const context = useActiveContextOptional();
  // Notifications are the caller's in the active organization: nothing to ask until one is chosen.
  const organizationId = context?.status === 'ready' ? context.active?.organizationId : undefined;
  // ONE READ PATH, KEYED BY ORGANIZATION. Every answer carries the organization it was asked for,
  // and is kept only while that organization is still the active one and the read is the latest.
  const [answer, setAnswer] = useState<{ readonly org: string; readonly page: Page } | null>(null);
  const [busy, setBusyState] = useState(false);
  // The same, for a callback that must know it at the moment it runs, not when it was made.
  const busyNow = useRef(false);
  const setBusy = useCallback((value: boolean) => {
    busyNow.current = value;
    setBusyState(value);
  }, []);
  const activeOrg = useRef<string | undefined>(undefined);
  const generation = useRef(0);
  // Marked read here, the server not yet told: a read that answers meanwhile does not bring them back.
  const pendingReads = useRef(new Set<string>());
  // A read waiting for the store to hold its organization: the store's change starts it.
  const waiting = useRef(false);

  // Stable: it reads the active organization from a ref, so whoever holds it calls the current one.
  const load = useCallback(() => {
    const org = activeOrg.current;
    const mine = ++generation.current;
    waiting.current = false;
    if (org === undefined) {
      setBusy(false);
      return;
    }
    setBusy(true);
    // Superseded by a later read, or asked for an organization no longer active: dropped. Its
    // organization not (or no longer) in the store api-client asks with: dropped too, and still
    // busy, since the store's change to it reads again.
    const current = () => {
      if (mine !== generation.current || org !== activeOrg.current) return false;
      if (storeOrg() === org) return true;
      waiting.current = true;
      return false;
    };
    void Promise.resolve().then(async () => {
      if (!current()) return;
      const next = await readUnread(org);
      if (!current()) return;
      setBusy(false);
      setAnswer((previous) => {
        if (next !== null) {
          return {
            org,
            page: {
              ...next,
              unread: next.unread.filter((item) => !pendingReads.current.has(item.id)),
            },
          };
        }
        // A re-read that failed keeps what is on screen; a first read that failed is unavailable.
        return previous?.org === org ? previous : { org, page: UNAVAILABLE };
      });
    });
  }, [setBusy]);

  useEffect(() => {
    activeOrg.current = organizationId;
    load();
    window.addEventListener('focus', load);
    const unsubscribe = subscribe(() => {
      if (waiting.current && storeOrg() === activeOrg.current) load();
    });
    return () => {
      window.removeEventListener('focus', load);
      unsubscribe();
      // A read still on its way for this organization is dropped, and is no longer busy.
      generation.current++;
      activeOrg.current = undefined;
      setBusy(false);
    };
  }, [organizationId, load, setBusy]);

  const page = answer !== null && answer.org === organizationId ? answer.page : null;

  const markRead = useCallback(
    (id: string) => {
      // A read already on its way is left to finish: it is not this one's to drop.
      const org = organizationId;
      if (org === undefined) return;
      const recount = page !== null && (page.capped || page.truncated);
      pendingReads.current.add(id);
      setAnswer((current) =>
        current === null
          ? current
          : {
              ...current,
              page: {
                ...current.page,
                unread: current.page.unread.filter((item) => item.id !== id),
              },
            },
      );
      void postRead(id, org).then((done) => {
        pendingReads.current.delete(id);
        // The organization changed before it was sent: its list is gone, and so is this mark.
        if (done === 'stale') return;
        // Read again when: the server did not mark it (it comes back); the count was a bound (it
        // is counted again); or a read was on its way, and may have answered before the server knew.
        if (!done || recount || busyNow.current) load();
      });
    },
    [load, organizationId, page],
  );

  // The active context's own read failed: there is no organization to ask for until it is read
  // again, so «Riprova» asks the context, and the notifications follow once it is ready.
  const contextFailed = context?.status === 'error';
  const reloadContext = context?.reload;
  const retry = useCallback(() => {
    if (contextFailed && reloadContext !== undefined) reloadContext();
    else load();
  }, [contextFailed, reloadContext, load]);

  const status: ExpenseNotificationsStatus =
    // While the active context is still loading there is an organization to wait for, not «none»;
    // when it failed, the notifications cannot be known either.
    context?.status === 'loading'
      ? 'loading'
      : context?.status === 'error'
        ? 'unavailable'
        : organizationId === undefined
          ? 'ready'
          : page === null
            ? 'loading'
            : page.available
              ? 'ready'
              : 'unavailable';
  const current = status === 'ready' && page !== null ? page : EMPTY;
  return {
    status,
    unread: current.unread,
    capped: current.capped,
    truncated: current.truncated,
    // A read on its way once there is something on screen: the list, or the unavailable line.
    refreshing: status !== 'loading' && busy,
    reload: load,
    retry,
    markRead,
  };
}

/** Where a notification leads: its note, or the month closings for a closing reminder. */
export function notificationPath(notification: ExpenseNotification): string {
  if (notification.reportId !== null) {
    return `/expenses/reports/${encodeURIComponent(notification.reportId)}`;
  }
  return notification.kind === 'month_closing_reminder' ? '/expenses/closings' : '/expenses';
}
